import { AssignmentTurnedInOutlined, ExpandMoreRounded } from "@mui/icons-material";
import {
  Accordion as MuiAccordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Divider,
  Link,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  styled,
  Menu,
  MenuItem,
} from "@mui/material";
import { ActionItemStatusPicker, AddSubStrategyItemModal, CreateActionItemModal, isWhitespace, ItemProperty } from "..";
import { DEFAULT_ACTION_ITEM_STATUS, Links, SupportingItemType } from "../../constants";
import { ActionItemTeamPlanSectionDto, SupportingActionItemsDto } from "../../http";
import { useState } from "react";

const Accordion = styled(MuiAccordion)<{ $backgroundColourOnHover?: string }>`
  background-color: transparent;
  border-radius: 0;
  margin-top: 0 !important;

  .MuiButtonBase-root {
    min-height: 0;
  }

  .MuiAccordionSummary-content {
    margin: 0;
  }
`;

interface IProps {
  items: SupportingActionItemsDto[];
  context: {
    teamName: string;
    teamSlug: string;
    actionItemGuid: string;
    actionItemDescription: string;
  };
  teamPlanSection: ActionItemTeamPlanSectionDto;
  isLoading?: boolean;
  onClick?: (type: SupportingItemType, strategicItemGuid: string) => void;
}

const SupportingStrategicItemsProperty = (props: IProps) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [isCreateActionItemModalVisible, setIsCreateActionItemModalVisible] = useState(false);
  const [isAddSubStrategyItemModalVisible, setIsAddSubStrategyItemModalVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  function handleClicked(event: React.MouseEvent, strategicItemGuid: string) {
    if (props.onClick) {
      event.preventDefault();
      props.onClick(SupportingItemType.ActionItem, strategicItemGuid);
    }
  }

  const itemsFromSameTeam = props.items.filter((item) => item.team.slug === props.context?.teamSlug);
  const itemsFromOtherTeams = props.items.filter((item) => item.team.slug !== props.context?.teamSlug);

  return (
    <ItemProperty
      labelIcon={<AssignmentTurnedInOutlined />}
      label="Strategic Items"
      labelProps={{ sx: { alignSelf: "start" } }}
    >
      <Stack spacing={0.5}>
        {props.isLoading ? (
          <Stack spacing={0.5}>
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} variant="rectangular" height={24} width={Math.random() * 175 + 75} />
            ))}
          </Stack>
        ) : (
          itemsFromSameTeam.map((actionItem) => (
            <Stack key={actionItem.actionItemGuid} direction="row">
              <ActionItemStatusPicker
                actionItemGuid={actionItem.actionItemGuid}
                status={actionItem.currentStatus ?? DEFAULT_ACTION_ITEM_STATUS}
                variant="button"
                disabled
                planGuid={""}
              />
              <Tooltip
                title={
                  <Typography>
                    {actionItem.team && (
                      <>
                        <strong>{actionItem.team.name}</strong>
                        <span> : </span>
                      </>
                    )}
                    <Link href={Links.ActionItemDetail(actionItem.actionItemGuid)}>{actionItem.description}</Link>
                  </Typography>
                }
                placement="right"
              >
                <Link
                  href={Links.ActionItemDetail(actionItem.actionItemGuid)}
                  onClick={(e) => handleClicked(e, actionItem.actionItemGuid)}
                >
                  {actionItem.description == null || isWhitespace(actionItem.description) ? (
                    <Typography variant="placeholder">Nothing here yet.</Typography>
                  ) : (
                    <Typography>{actionItem.description}</Typography>
                  )}
                </Link>
              </Tooltip>
            </Stack>
          ))
        )}

        <Button
          variant="tertiary"
          startIcon={<AssignmentTurnedInOutlined />}
          endIcon={<ExpandMoreRounded />}
          sx={{ alignSelf: "start" }}
          onClick={(event) => {
            event.stopPropagation();
            setMenuAnchor(event.currentTarget);
          }}
        >
          Add SubItem
        </Button>

        {!props.isLoading && itemsFromOtherTeams.length > 0 && (
          <>
            <Divider />
            <Accordion disableGutters expanded={isExpanded} onChange={() => setIsExpanded(!isExpanded)}>
              <AccordionSummary expandIcon={<ExpandMoreRounded />} sx={{ px: 0, pt: 0.5 }}>
                <Typography variant="caption" textTransform="uppercase">
                  {`From Other Teams${!isExpanded ? ` (${itemsFromOtherTeams.length})` : ""}`}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0, pt: 0.5 }}>
                <Stack spacing={0.5}>
                  {itemsFromOtherTeams.map((actionItem) => (
                    <Stack key={actionItem.actionItemGuid} direction="row">
                      <ActionItemStatusPicker
                        actionItemGuid={actionItem.actionItemGuid}
                        status={actionItem.currentStatus ?? DEFAULT_ACTION_ITEM_STATUS}
                        variant="button"
                        disabled
                        planGuid={""}
                      />
                      <Tooltip
                        title={
                          <Typography>
                            {actionItem.team && (
                              <>
                                <strong>{actionItem.team.name}</strong>
                                <span> : </span>
                              </>
                            )}
                            <Link href={Links.ActionItemDetail(actionItem.actionItemGuid)}>
                              {actionItem.description}
                            </Link>
                          </Typography>
                        }
                        placement="right"
                      >
                        <Link
                          href={Links.ActionItemDetail(actionItem.actionItemGuid)}
                          onClick={(e) => handleClicked(e, actionItem.actionItemGuid)}
                        >
                          {actionItem.description == null || isWhitespace(actionItem.description) ? (
                            <Typography variant="placeholder">Nothing here yet.</Typography>
                          ) : (
                            <Typography>{actionItem.description}</Typography>
                          )}
                        </Link>
                      </Tooltip>
                    </Stack>
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          </>
        )}
      </Stack>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={(e: React.PointerEvent) => {
          setMenuAnchor(null);
          e.stopPropagation();
        }}
      >
        <MenuItem
          onClick={(e) => {
            setMenuAnchor(null);
            setIsCreateActionItemModalVisible(true);
            e.stopPropagation();
          }}
        >
          New
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            setIsAddSubStrategyItemModalVisible(true);
            setMenuAnchor(null);
          }}
        >
          Existing
        </MenuItem>
      </Menu>

      <CreateActionItemModal
        visible={isCreateActionItemModalVisible}
        teamPlanSection={props.teamPlanSection}
        context={props.context}
        onCreated={() => {
          setIsCreateActionItemModalVisible(false);
        }}
        onCancelled={() => setIsCreateActionItemModalVisible(false)}
      />

      <AddSubStrategyItemModal
        visible={isAddSubStrategyItemModalVisible}
        teamPlanSection={props.teamPlanSection}
        context={props.context}
        onCreated={() => {
          setIsAddSubStrategyItemModalVisible(false);
        }}
        onCancelled={() => setIsAddSubStrategyItemModalVisible(false)}
      />
    </ItemProperty>
  );
};

export { SupportingStrategicItemsProperty };
